import { useState } from 'react'
import { ObjectiveStatus, ObjectiveDto, ObjectiveInput } from '../../types'
import { newObjective } from '../../modules/appraisal/okr/util'
import useObjectiveInMeeting from './useObjectiveInMeeting'
import { useAuth } from '../../context/auth-context'
import { useDateTime } from '../../shared/useDateTime'
import { showNotification } from '../../components/custom_components/Notification/notification'
import { useTranslation } from 'react-i18next'

//todo, some hooks are stacked here and imho it's not a good idea. I copied over the implementation and made it work but the reason why it is build this way escapes me

interface Props {
  meetingId: string
  initialValue?: ObjectiveInput
}

export default ({
  meetingId,
  initialValue = undefined,
}: Props): {
  objective: ObjectiveInput | undefined
  open: boolean
  close: () => void
  handleObjectiveChange: (isManagerPreparation: boolean, objectiveData?: ObjectiveDto) => void
  handleObjectiveDismiss: (objectiveId: number) => Promise<void>
  handleObjectiveReactivate: (objectiveId: number) => Promise<void>
  handleObjectiveDuplicate: (objectiveGlobalId: string) => Promise<void>
  handleSetAttachment: (values: ObjectiveInput, filename?: string) => void
  handleDeleteAttachment: (values: ObjectiveInput) => void
  isMonetary: boolean | undefined
  onMonetaryChange: (isMonetaryObjective: boolean, objectiveData: ObjectiveInput) => void
} => {
  const [open, changeOpen] = useState(false)
  const { user } = useAuth()
  const { calcStartOfNextNYear, calcEndOfNextNYear } = useDateTime(user)
  const { objective, setObjective, dismissObjectiveApi, reactivateObjectiveApi, duplicateObjectiveApi } = useObjectiveInMeeting({
    meetingId: meetingId,
    initialValue: initialValue,
  })

  const [t] = useTranslation()

  const [isMonetary, setIsMonetary] = useState(objective && !!objective.targetAmount)

  const onMonetaryChange = (isMonetaryObjective: boolean, objectiveData: ObjectiveInput): void => {
    const current = objectiveData ? objectiveData : objective

    if (isMonetaryObjective && current && current.status === ObjectiveStatus.MarkedAsDone) {
      current.status = ObjectiveStatus.Approved
    } else if (isMonetaryObjective && objectiveData) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      current!.achievedAmount = null
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      current!.targetAmount = null
    }
    if (current) {
      setObjective({ ...current })
    }
    setIsMonetary(isMonetaryObjective)
  }

  const handleObjectiveChange = (isManagerPreparation: boolean, objectiveData?: ObjectiveDto): void => {
    const current: ObjectiveInput = objectiveData
      ? ({ ...objectiveData } as ObjectiveInput)
      : newObjective(isManagerPreparation, {
          calcStartOfNextNYear: calcStartOfNextNYear,
          calcEndOfNextNYear: calcEndOfNextNYear,
        })

    setObjective(current)
    changeOpen(true)
  }

  const handleSetAttachment = (values: ObjectiveInput, filename?: string): void => {
    const current = values ? values : objective
    if (current) {
      setObjective({ ...current, attachment: filename })
    }
  }

  const handleDeleteAttachment = (values: ObjectiveInput): void => {
    const current = values ? values : objective
    if (current) {
      setObjective({ ...current, attachment: null, attachmentDownloadToken: null })
    }
  }

  const handleObjectiveDismiss = async (objectiveId: number): Promise<void> => {
    const { errors } = await dismissObjectiveApi(objectiveId)
    if (!!errors && !!errors.length) {
      showNotification({
        type: 'error',
        message: t('appraisalMeeting.notifications.target.dismiss.error.title'),
        description: t('appraisalMeeting.notifications.target.dismiss.error.description'),
      })
    }
    close()
  }
  const handleObjectiveReactivate = async (objectiveId: number): Promise<void> => {
    const { errors } = await reactivateObjectiveApi(objectiveId)
    if (!!errors && !!errors.length) {
      showNotification({
        type: 'error',
        message: t('appraisalMeeting.notifications.target.reactivate.error.title'),
        description: t('appraisalMeeting.notifications.target.reactivate.error.description'),
      })
    }
    close()
  }

  const handleObjectiveDuplicate = async (objectiveGlobalId: string): Promise<void> => {
    const { errors } = await duplicateObjectiveApi(objectiveGlobalId)
    if (!!errors && !!errors.length) {
      showNotification({
        type: 'error',
        message: t('appraisalMeeting.notifications.target.duplicate.error.title'),
        description: t('appraisalMeeting.notifications.target.duplicate.error.description'),
      })
    }
    close()
  }

  // ATTENTION: we need this because Fromik resets the form by using deep compare
  // TODO: investigate if there is a better way then setting objective
  const close = () => {
    setObjective(undefined)
    setIsMonetary(false)
    changeOpen(false)
  }

  return {
    objective,
    open,
    close,
    handleObjectiveChange,
    handleObjectiveDismiss,
    handleObjectiveReactivate,
    handleObjectiveDuplicate,
    handleSetAttachment,
    handleDeleteAttachment,
    isMonetary,
    onMonetaryChange,
  }
}
